import { useCallback, useEffect, useState } from "react";
import { ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { unwrap, formatIST, ISO_DATE_RE, parseUTCDate } from "../../lib/helpers";
import { KVTable } from "../../components/DataViews";
import { StatCard } from "../../components/StatCard";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";

const HIGHLIGHT_RE = /billing|renewal|expir|started|balance|period|cancel|payment/i;

function formatHighlight(v) {
  if (typeof v === "number" && v > 1e9 && v < 2e10) return formatIST(new Date(v * 1000));
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "string" && ISO_DATE_RE.test(v.trim())) {
    const d = parseUTCDate(v);
    if (d) return formatIST(d);
  }
  return String(v);
}

export function BillingTab({ businessId, projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard/projects/billing", {
        params: { wydely_business_id: businessId, wydely_project_id: projectId },
      });
      setData(unwrap(res.data));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [businessId, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading)
    return (
      <div className="space-y-3" data-testid="billing-loading">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );

  const highlights = Object.entries(data || {})
    .filter(([k, v]) => HIGHLIGHT_RE.test(k) && (v === null || ["string", "number", "boolean"].includes(typeof v)))
    .slice(0, 8);

  return (
    <div className="space-y-6" data-testid="billing-tab">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={load} data-testid="billing-refresh">
          <ArrowClockwise className="mr-2 h-4 w-4" /> Refresh
        </Button>
      </div>

      {highlights.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {highlights.map(([k, v]) => (
            <StatCard key={k} label={k.replace(/_/g, " ")} value={v === null ? "—" : formatHighlight(v)} tone="primary" testId={`billing-stat-${k.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} />
          ))}
        </div>
      )}

      <section className="space-y-3">
        <h3 className="text-base font-display font-bold tracking-tight">Wydely & AiSensy billing</h3>
        <KVTable data={data} testId="billing-table" />
      </section>
    </div>
  );
}
